import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Lock, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { authUtils } from "@/utils/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(location.state?.error || null);

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      // Check if we have a stored token
      if (authUtils.hasToken()) {
        try {
          const response = await axios.get(`${API}/auth/me`, authUtils.getAuthConfig());
          if (response.data?.user_id) {
            navigate("/admin/dashboard", { replace: true });
            return;
          }
        } catch (error) {
          // Token invalid, clear it
          authUtils.clearToken();
        }
      }
      setChecking(false);
    };
    checkAuth();
  }, [navigate]);

  // Check for error in location state
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
  }, [location.state]);

  const handleGoogleLogin = () => {
    setError(null);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-dream-bg flex items-center justify-center" data-testid="admin-login-loading">
        <Loader2 className="w-8 h-8 text-dream-violet animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dream-bg flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass rounded-2xl p-8 md:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/w4q79k1x_Dream%20Maker-4.png" 
              alt="Dream Makers"
              className="h-16 w-auto"
            />
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-dream-violet/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-dream-violet" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Admin Dashboard
            </h1>
            <p className="text-white/60 text-sm">
              Sign in with your authorized Google account
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 text-sm font-medium">Sign in failed</p>
                <p className="text-red-400/80 text-xs mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Login Button */}
          <Button
            onClick={handleGoogleLogin}
            className="w-full bg-white text-gray-800 hover:bg-gray-100 rounded-full py-6 font-semibold flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>

          {/* Info */}
          <div className="mt-8 pt-6 border-t border-dream-border">
            <p className="text-white/40 text-xs text-center">
              Access restricted to authorized administrators only.
              <br />
              Contact support if you need access.
            </p>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <a 
            href="/" 
            className="text-white/50 text-sm hover:text-dream-violet transition-colors"
          >
            ← Back to website
          </a>
        </div>
      </motion.div>
    </div>
  );
}
